import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProductList, getProductDetails } from '../services/productService';
import { getUserInfo } from '../utils/tokenManager';
import ProductTable from '../components/ProductTable';
import ProductSettingsModal from '../components/ProductSettingsModal';
import NewProductModal from '../components/NewProductModal';
import { PlusIcon } from '@heroicons/react/24/solid';
import { saveProductBasicSettings } from '../services/productBasicSettings';
import { ensureSingleItemBookingFields } from '../services/singleItemBookingFields';

/** 이 화면이 다루는 상품 타입. 목록 필터와 신규 등록이 같은 값을 써야 등록한 것이 목록에 뜬다. */
const PRODUCT_TYPE_NAME = '단품';

const SingleProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isNewProductOpen, setIsNewProductOpen] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const reloadList = async () => {
    try {
      setLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      if (!companyNo || !userNo) { setError('로그인이 필요합니다.'); return; }
      const list = await getProductList(companyNo, userNo);
      setProducts(Array.isArray(list) ? list : []);
    } catch (e) {
      setError('상품 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadList();
  }, []);

  const openBasicSettings = async (p) => {
    try {
      setSettingsLoading(true);
      const { companyNo } = getUserInfo() || {};
      const details = await getProductDetails(p?.상품번호 || p?.상품코드, companyNo);
      if (details) {
        setSelectedProduct(details);
        setIsSettingsOpen(true);
      } else {
        alert('상품 상세 정보를 찾을 수 없습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('상품 상세 정보를 불러오는데 실패했습니다.');
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsSettingsOpen(false);
    setSelectedProduct(null);
  };

  const handleSaveSettings = async (updatedProduct) => {
    try {
      setSettingsLoading(true);
      const { companyNo, userNo } = getUserInfo() || {};
      const productNo = await saveProductBasicSettings({
        original: selectedProduct || {},
        updated: updatedProduct,
        companyNo,
        userNo,
      });

      const refreshed = await getProductDetails(productNo, companyNo).catch(() => null);
      if (refreshed) setSelectedProduct(refreshed);
      await reloadList();
      alert('성공적으로 저장되었습니다.');

      return true;
    } catch (e) {
      console.error('Failed to save product details:', e);
      alert('저장에 실패했습니다. 다시 시도해주세요.');

      return false;
    } finally {
      setSettingsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return (products || [])
      .filter(p => String(p?.상품타입 || '') === PRODUCT_TYPE_NAME)
      .filter(p => String(p?.상품종류코드 || '').trim() !== '001001')
      .filter(p => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (
          String(p?.상품명 || '').toLowerCase().includes(s) ||
          String(p?.도시 || '').toLowerCase().includes(s) ||
          String(p?.상품코드 || '').toLowerCase().includes(s)
        );
      });
  }, [products, search]);

  return (
    <div className="h-full min-h-0 p-4 flex flex-col">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between rounded-2xl bg-white p-5">
        <h1 className="text-[17px] font-bold text-gray-900">일반상품(단품)</h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="block w-full h-9 pl-3 pr-3 rounded-lg border border-gray-300 bg-white placeholder-gray-400 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none"
              placeholder="상품명/도시/코드 검색..."
            />
          </div>
          <button
            type="button"
            onClick={() => setIsNewProductOpen(true)}
            className="w-full sm:w-auto inline-flex items-center justify-center h-9 px-3.5 whitespace-nowrap text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
            새 단품 등록
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600"></div>
            <p className="text-sm text-gray-500">상품 목록을 불러오는 중...</p>
          </div>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="rounded-2xl bg-white p-5 mt-4 flex-1 min-h-0 overflow-auto">
          <ProductTable
            products={filtered}
            enableColumnFilters
            singleDateFilter
            dateOnly
            disablePagination
            hideLocationTypeColumns
            showNoteBadgeColumns
            showOrdersButton={false}
            actionsBelowRow
            onOpenPriceStock={(p) => navigate(`/general/price-stock/${p?.상품번호 || ''}`)}
            onOpenDetailSettings={(p) => navigate(`/general/detail-settings/${p?.상품번호 || ''}`)}
            onOpenSettings={openBasicSettings}
            settingsButtonLabel="기본설정"
          />
        </div>
      )}

      {/* 신규 단품 등록 — 타입을 단품으로 고정해야 등록 후 이 목록에 다시 나타난다.
          단품은 도시·기간이 의미 없어 상품명만 받고, 나머지는 기본값으로 넣는다. */}
      {isNewProductOpen && (
        <NewProductModal
          open={isNewProductOpen}
          onClose={() => setIsNewProductOpen(false)}
          fixedProductTypeName={PRODUCT_TYPE_NAME}
          minimalFields
          onCreated={async ({ productNo }) => {
            setIsNewProductOpen(false);
            // 예약시 받을 정보에 주소 3종을 붙인다. 실패해도 상품은 이미 만들어졌으므로
            // 화면 이동은 막지 않는다 — 기본설정에서 직접 추가하면 된다.
            try {
              await ensureSingleItemBookingFields(productNo);
            } catch (e) {
              console.error('단품 주소 필드 자동 추가 실패:', e);
              alert('상품은 만들어졌지만 주소 항목(우편번호/주소/상세주소)을 자동으로 넣지 못했습니다. 기본설정 > 예약시 받을 정보에서 추가해 주세요.');
            }
            navigate(`/general/detail-settings/${productNo}`);
          }}
        />
      )}

      {/* 기본설정 모달 */}
      {isSettingsOpen && (
        <ProductSettingsModal
          product={selectedProduct}
          onClose={handleCloseModal}
          onSave={handleSaveSettings}
          loading={settingsLoading}
          hideBasicCity
          hideBasicDuration
          hideDetailTab
        />
      )}
    </div>
  );
};

export default SingleProducts;
